import { TypedJSONObject, JSONObject } from '../types';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { token } from '@trello/session-cookie';
import {
  MutationCopyBoardToOrgArgs,
  Organization_Stats_Cards_GroupBy,
} from '../../generated';

export const organizationNewBillableGuestsResolver = async (
  organization: {
    id: string;
  },
  args: { boardId: string },
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/newBillableGuests/${args.boardId}`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, info, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationOwnedPluginsResolver = async (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
): Promise<TypedJSONObject> => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/ownedPlugins`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([401, 404].includes(response.status)) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, info, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationLabelNamesResolver = async (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/stats/labelNames/`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    const mappedModel =
      Object.entries(model['data'] as JSONObject)
        // Map the labelNames returned from Apollo to LabelFilterOrgLabels
        .map(([name, orgLabel]: [string, JSONObject]) => ({
          name,
          ...orgLabel,
        })) || [];

    return prepareDataForApolloCache(
      {
        incomplete: model.incomplete,
        stats: mappedModel,
      },
      info,
      'Organization_Stats',
    );
  } catch (err) {
    console.error(err);
    return model;
  }
};

const GroupByToIdField: Record<Organization_Stats_Cards_GroupBy, string> = {
  [Organization_Stats_Cards_GroupBy.Label]: 'idLabel',
  [Organization_Stats_Cards_GroupBy.LabelName]: 'labelName',
  [Organization_Stats_Cards_GroupBy.Member]: 'idMember',
};

const assignGroupById = (
  obj: JSONObject,
  id: string,
  groupBy: Organization_Stats_Cards_GroupBy,
) => {
  const idField = GroupByToIdField[groupBy];
  return idField ? { [idField]: id, ...obj } : obj;
};

export const organizationCardStatsResolver = async (
  organizationStats: {
    id: string;
  },
  args: {
    groupBy?: Organization_Stats_Cards_GroupBy;
    idBoards?: string[];
    idMembers?: string[];
    idLabels?: string[];
    dueDateSince?: string;
    dueDateUntil?: string;
  },
  context: object,
  info: QueryInfo,
) => {
  const {
    groupBy,
    idBoards,
    idMembers,
    idLabels,
    dueDateUntil,
    dueDateSince,
  } = args;
  const params = new URLSearchParams({
    ...(groupBy && { groupBy: groupBy }),
    ...(idBoards && idBoards.length > 0 && { idBoards: idBoards.join(',') }),
    ...(idMembers &&
      idMembers.length > 0 && { idMembers: idMembers.join(',') }),
    ...(idLabels && idLabels.length > 0 && { idLabels: idLabels.join(',') }),
    ...(dueDateUntil && { dueDateUntil }),
    ...(dueDateSince && { dueDateSince }),
    includeIds: 'overdue,upcoming',
  });
  const url = `/1/organizations/${
    organizationStats.id
  }/stats/cards?${params.toString()}`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  const json = await response.json();

  const cards = groupBy
    ? Object.entries(json['data'] as JSONObject).map(
        ([key, value]: [string, JSONObject]) => {
          return assignGroupById(value, key, groupBy);
        },
      )
    : [json['data']];

  return prepareDataForApolloCache(
    {
      incomplete: json.incomplete,
      stats: cards,
    },
    info,
    'Organization_Stats',
  );
};

export const organizationStatsResolver = (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) => {
  return prepareDataForApolloCache(
    {
      id: organization.id,
    },
    info,
    'Organization',
  );
};

export const createOrganization = async (
  obj: object,
  args: {
    displayName: string;
    type: string;
    desc?: string;
    enterprise?: string;
  },
  context: object,
  info: QueryInfo,
) => {
  try {
    const response = await fetch('/1/organizations', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(args || {}),
        token,
      }),
    });

    if (response.ok) {
      return prepareDataForApolloCache(await response.json(), info);
    }

    const responseError = await response.json();

    if (responseError.message) {
      throw new Error(responseError.message);
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }
  } catch (err) {
    throw new Error(
      'There was a problem creating your team. Refresh the page and try again.',
    );
  }
};

export async function copyBoardToOrg(
  obj: object,
  args: MutationCopyBoardToOrgArgs,
  context: object,
  info: QueryInfo,
) {
  let response = await fetch(`/1/boards/${args.boardId}`);
  const copy = await response.json();

  const searchParams = new URLSearchParams();

  searchParams.set('token', token || '');
  searchParams.set('name', copy.name);
  searchParams.set('idOrganization', args.organizationId);
  searchParams.set('idBoardSource', args.boardId);
  searchParams.set('creationMethod', 'assisted');
  searchParams.set('keepFromSource', 'cards');
  searchParams.set('prefs_permissionLevel', 'private');

  response = await fetch(`/1/boards/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: searchParams,
  });

  const board = await response.json();

  return prepareDataForApolloCache(board, info);
}

export const addMembersToOrg = async (
  obj: object,
  {
    orgId,
    users,
    invitationMessage,
  }: {
    orgId: string;
    users: [{ email?: string; id?: string }];
    invitationMessage: string;
  },
  context: object,
  info: QueryInfo,
) => {
  try {
    await Promise.all(
      users.map((user) => {
        return fetch(
          `/1/organizations/${orgId}/members${user.id ? `/${user.id}` : ''}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...(user.id ? {} : { email: user.email }),
              type: 'normal',
              invitationMessage,
              token,
            }),
          },
        );
      }),
    );

    return prepareDataForApolloCache({ success: true }, info);
  } catch (err) {
    return prepareDataForApolloCache(
      {
        success: false,
        error: err.message,
      },
      info,
    );
  }
};
